import { CalendarPlus, MapPin } from 'lucide-react'
import { useEffect, useState } from 'react'

import type {
  DayOfWeek,
  DualControlResult,
  GroupDetail,
  MeetingFrequency,
  MeetingGenerationResult,
} from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatShortDate } from '@/lib/datetime'

import {
  DAY_OF_WEEK_OPTIONS,
  MEETING_FREQUENCY_OPTIONS,
  describeSchedule,
} from './constants'
import { DualControlNotice, DualControlOutcome } from './dual-control'
import { GroupActionDialog } from './group-action-dialog'
import { useScheduleGroupMeetings, useSetGroupMeetingLocation } from './mutations'
import { GROUP_PERMISSIONS, useGroupPermissions } from './permissions'

const MINIMUM_REASON_LENGTH = 5

/** The API's own bounds, mirrored so a refusal is avoided rather than explained afterwards. */
const MIN_RADIUS_METRES = 20
const MAX_RADIUS_METRES = 2000
const MAX_OCCURRENCES = 104

const today = () => new Date().toISOString().slice(0, 10)

/**
 * Where the group meets, and how far from that pin a collection may be taken.
 *
 * Dual-controlled, and the reason is easy to miss on a screen that looks like reference data:
 * the geofence is what unlocks a collection sheet in the field, so moving the pin or widening the
 * radius changes where money may be collected. That makes it control configuration.
 *
 * Only the "define a location" form is offered. The contract's other form points the group at an
 * existing location by id, and there is no endpoint that lists locations — so a picker here would
 * be a control with nothing to put in it. A shared location is pointed at by supplying its id
 * through the API directly until that list exists.
 *
 * Typing coordinates at a desk is right here and wrong on a customer address, which is the same
 * distinction ADR-004 draws: this says where the institution collects money, that says where a
 * person sleeps.
 */
export function MeetingLocationDialog({
  group,
  open,
  onOpenChange,
}: {
  group: GroupDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useGroupPermissions()
  const [name, setName] = useState('')
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [radius, setRadius] = useState('100')
  const [address, setAddress] = useState('')
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  const save = useSetGroupMeetingLocation(group.id)

  useEffect(() => {
    if (!open) {
      setName('')
      setLatitude('')
      setLongitude('')
      setRadius('100')
      setAddress('')
      setReason('')
      setResult(null)
      save.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const latitudeValue = Number(latitude)
  const longitudeValue = Number(longitude)
  const radiusValue = Number(radius)

  const coordinatesValid =
    latitude !== '' &&
    longitude !== '' &&
    Number.isFinite(latitudeValue) &&
    Number.isFinite(longitudeValue) &&
    Math.abs(latitudeValue) <= 90 &&
    Math.abs(longitudeValue) <= 180

  const radiusValid =
    Number.isInteger(radiusValue) &&
    radiusValue >= MIN_RADIUS_METRES &&
    radiusValue <= MAX_RADIUS_METRES

  const trimmedReason = reason.trim()

  return (
    <GroupActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title={group.meetingLocation ? 'Move the meeting location' : 'Set the meeting location'}
      description={
        group.meetingLocation
          ? `${group.name} currently meets at ${group.meetingLocation.name}, within ${String(group.meetingLocation.geofenceRadiusMetres)} m.`
          : `${group.name} has no meeting location, so no meetings can be scheduled yet.`
      }
      notice={
        <DualControlNotice
          action="Changing where a group meets"
          approvePermission={GROUP_PERMISSIONS.locationManageApprove}
          canCountersign={permissions.canCountersignLocationChange}
        />
      }
      outcome={
        result ? (
          <DualControlOutcome
            result={result}
            awaitingDescription={`Moving ${group.name} to ${name || 'the new location'} is waiting for a countersignature.`}
            appliedDescription={`${group.name} now meets at ${name || 'the new location'}. A register already taken at the old place keeps the fix it was taken against.`}
          />
        ) : null
      }
      confirmLabel="Save location"
      isPending={save.isPending}
      disabled={
        name.trim() === '' ||
        address.trim() === '' ||
        !coordinatesValid ||
        !radiusValid ||
        trimmedReason.length < MINIMUM_REASON_LENGTH
      }
      error={save.isError ? save.error : null}
      onConfirm={() => {
        save.mutate(
          {
            name: name.trim(),
            latitude: latitudeValue,
            longitude: longitudeValue,
            geofenceRadiusMetres: radiusValue,
            address: address.trim(),
            reason: trimmedReason,
          },
          { onSuccess: setResult },
        )
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="location-name">Name</Label>
        <Input
          id="location-name"
          value={name}
          autoComplete="off"
          placeholder="Makola Market shed 4"
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="location-address">Address</Label>
        <Input
          id="location-address"
          value={address}
          autoComplete="off"
          placeholder="Kojo Thompson Road, Accra"
          onChange={(event) => setAddress(event.target.value)}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="location-latitude">Latitude</Label>
          <Input
            id="location-latitude"
            value={latitude}
            inputMode="decimal"
            autoComplete="off"
            placeholder="5.5461"
            onChange={(event) => setLatitude(event.target.value)}
            aria-invalid={latitude !== '' && !coordinatesValid}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="location-longitude">Longitude</Label>
          <Input
            id="location-longitude"
            value={longitude}
            inputMode="decimal"
            autoComplete="off"
            placeholder="-0.2094"
            onChange={(event) => setLongitude(event.target.value)}
            aria-invalid={longitude !== '' && !coordinatesValid}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="location-radius">Radius, metres</Label>
          <Input
            id="location-radius"
            value={radius}
            inputMode="numeric"
            autoComplete="off"
            onChange={(event) => setRadius(event.target.value)}
            aria-invalid={radius !== '' && !radiusValid}
          />
        </div>
      </div>

      <p className="text-xs text-muted">
        Between {MIN_RADIUS_METRES} m and {MAX_RADIUS_METRES} m. The radius is measured from this pin,
        and a field collection outside it is refused — so a generous radius is not a kindness, it is
        a wider area in which money may be collected unsupervised.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="location-reason">Reason</Label>
        <Input
          id="location-reason"
          value={reason}
          autoComplete="off"
          onChange={(event) => setReason(event.target.value)}
          aria-describedby="location-reason-help"
        />
        <p id="location-reason-help" className="text-xs text-muted">
          Read by whoever countersigns this. &ldquo;Group moved to the covered shed&rdquo; is a
          reason; &ldquo;update&rdquo; is not.
        </p>
      </div>
    </GroupActionDialog>
  )
}

/**
 * Putting meetings on the calendar.
 *
 * Two things about this are worth saying on the screen rather than in a refusal afterwards. A
 * schedule needs a location first, because a meeting with no place is not a meeting anyone can be
 * sent to. And setting a schedule cancels what the previous one had put on the calendar from the
 * new start date, where those meetings have not yet happened — a group whose day moves from
 * Wednesday to Thursday must not be left with both sets, or the officer is sent twice and the
 * attendance denominator doubles for every week of overlap.
 */
export function ScheduleMeetingsDialog({
  group,
  open,
  onOpenChange,
}: {
  group: GroupDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [frequency, setFrequency] = useState<MeetingFrequency>('Weekly')
  const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek>('Wednesday')
  const [dayOfMonth, setDayOfMonth] = useState('15')
  const [startTime, setStartTime] = useState('10:00')
  const [effectiveFrom, setEffectiveFrom] = useState(today)
  const [occurrences, setOccurrences] = useState('26')
  const [result, setResult] = useState<MeetingGenerationResult | null>(null)

  const schedule = useScheduleGroupMeetings(group.id)

  useEffect(() => {
    if (!open) {
      setFrequency('Weekly')
      setDayOfWeek('Wednesday')
      setDayOfMonth('15')
      setStartTime('10:00')
      setEffectiveFrom(today())
      setOccurrences('26')
      setResult(null)
      schedule.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const monthly = frequency === 'Monthly'
  const dayOfMonthValue = Number(dayOfMonth)
  const occurrencesValue = Number(occurrences)

  // 28 rather than 31: a group that meets "on the 31st" does not meet in February, and a schedule
  // whose meaning changes with the length of the month is one the field cannot rely on.
  const dayOfMonthValid =
    Number.isInteger(dayOfMonthValue) && dayOfMonthValue >= 1 && dayOfMonthValue <= 28

  const occurrencesValid =
    Number.isInteger(occurrencesValue) && occurrencesValue >= 1 && occurrencesValue <= MAX_OCCURRENCES

  const hasLocation = group.meetingLocation !== null
  const current = group.schedules[0]

  return (
    <GroupActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Schedule meetings"
      description={
        current
          ? `${group.name} currently meets ${describeSchedule(current).toLowerCase()}.`
          : `${group.name} has nothing on the calendar yet.`
      }
      outcome={
        result ? (
          <div className="space-y-3">
            <Alert tone="success">
              <AlertTitle>
                {result.generated.length} meeting{result.generated.length === 1 ? '' : 's'} on the
                calendar
              </AlertTitle>
              <AlertDescription>
                {describeSchedule(result.schedule)}, from{' '}
                {formatShortDate(result.schedule.effectiveFrom)}.
                {result.generated[0]
                  ? ` The first is ${formatShortDate(result.generated[0].scheduledDate)}.`
                  : ''}
              </AlertDescription>
            </Alert>

            {result.skipped.length > 0 ? (
              <Alert tone="warning">
                <AlertTitle>
                  {result.skipped.length} date{result.skipped.length === 1 ? '' : 's'} could not be
                  placed
                </AlertTitle>
                <AlertDescription>
                  A meeting landing on a holiday rolls forward, and where an unbroken run of
                  holidays would push it onto the next scheduled meeting it is reported here rather
                  than generated on top of its successor.
                  <ul className="mt-1 list-inside list-disc">
                    {result.skipped.map((entry) => (
                      <li key={entry.scheduledDate}>{formatShortDate(entry.scheduledDate)}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        ) : null
      }
      confirmLabel="Generate meetings"
      isPending={schedule.isPending}
      disabled={
        !hasLocation ||
        startTime === '' ||
        effectiveFrom === '' ||
        !occurrencesValid ||
        (monthly && !dayOfMonthValid)
      }
      error={schedule.isError ? schedule.error : null}
      onConfirm={() => {
        schedule.mutate(
          {
            frequency,
            dayOfWeek: monthly ? null : dayOfWeek,
            dayOfMonth: monthly ? dayOfMonthValue : null,
            startTime,
            effectiveFrom,
            effectiveTo: null,
            occurrences: occurrencesValue,
          },
          { onSuccess: setResult },
        )
      }}
    >
      {hasLocation ? null : (
        <Alert tone="warning">
          <AlertTitle>Set the meeting location first</AlertTitle>
          <AlertDescription>
            Meetings cannot be scheduled before the group has a place to meet. Set the location, then
            come back — the two are separate because moving a location is countersigned and putting
            dates on a calendar is not.
          </AlertDescription>
        </Alert>
      )}

      {current ? (
        <Alert tone="info">
          <AlertTitle>This replaces the current schedule</AlertTitle>
          <AlertDescription>
            Meetings the current schedule put on the calendar from {formatShortDate(effectiveFrom)}{' '}
            onwards are cancelled where they have not yet happened. Meetings already held, missed, or
            in progress are untouched — they are records of what happened, not plans.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="schedule-frequency">Frequency</Label>
          <Select
            value={frequency}
            onValueChange={(value) => setFrequency(value as MeetingFrequency)}
          >
            <SelectTrigger id="schedule-frequency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEETING_FREQUENCY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {monthly ? (
          <div className="space-y-1.5">
            <Label htmlFor="schedule-day-of-month">Day of the month</Label>
            <Input
              id="schedule-day-of-month"
              value={dayOfMonth}
              inputMode="numeric"
              onChange={(event) => setDayOfMonth(event.target.value)}
              aria-invalid={!dayOfMonthValid}
              aria-describedby="schedule-day-of-month-help"
            />
            <p id="schedule-day-of-month-help" className="text-xs text-muted">
              1 to 28. A group that meets on the 31st does not meet in February.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="schedule-day-of-week">Day of the week</Label>
            <Select value={dayOfWeek} onValueChange={(value) => setDayOfWeek(value as DayOfWeek)}>
              <SelectTrigger id="schedule-day-of-week">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_OF_WEEK_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="schedule-start-time">Start time</Label>
          <Input
            id="schedule-start-time"
            type="time"
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="schedule-effective-from">Effective from</Label>
          <Input
            id="schedule-effective-from"
            type="date"
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="schedule-occurrences">Meetings to generate</Label>
          <Input
            id="schedule-occurrences"
            value={occurrences}
            inputMode="numeric"
            onChange={(event) => setOccurrences(event.target.value)}
            aria-invalid={!occurrencesValid}
            aria-describedby="schedule-occurrences-help"
          />
          <p id="schedule-occurrences-help" className="text-xs text-muted">
            1 to {MAX_OCCURRENCES} — two years of weekly meetings. Generating an unbounded horizon
            turns the next schedule change into a mass update of rows nobody has attended.
          </p>
        </div>
      </div>
    </GroupActionDialog>
  )
}

/** The two calendar controls, each behind the permission its endpoint authorises against. */
export function ScheduleActions({
  group,
  onSetLocation,
  onSchedule,
}: {
  group: GroupDetail
  onSetLocation: () => void
  onSchedule: () => void
}) {
  const permissions = useGroupPermissions()
  const dissolved = group.status === 'Dissolved'

  return (
    <div className="flex flex-wrap items-center gap-2">
      {permissions.canManageLocation ? (
        <Button variant="secondary" size="sm" onClick={onSetLocation} disabled={dissolved}>
          <MapPin />
          {group.meetingLocation ? 'Move location' : 'Set location'}
        </Button>
      ) : null}

      {permissions.canScheduleMeetings ? (
        <Button variant="secondary" size="sm" onClick={onSchedule} disabled={dissolved}>
          <CalendarPlus />
          Schedule meetings
        </Button>
      ) : null}
    </div>
  )
}
